import React, { createContext, useContext, ReactNode, useEffect } from 'react'
import { CrmFunnelItem } from '@/api/types/crm/crmFunnels.types'
import { Field, LeadState, Manager, Status, TrafficChannel } from '@/api/types/crm/crm.types'
import { useAsync } from 'react-use'
import { crmApi, crmTrafficChannelApi, wazzupIntegrationApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { WazzupGetFields } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupIntegration.types'
import {
  VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
  type VkIntegrationLeadChatAvailability,
} from './utils/vkIntegrationLeadChat'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'

export interface PreloadingData {
  managers: Manager[]
  statuses: Status[]
  fields: Field[]
  funnels: CrmFunnelItem[]
  trafficChannels: TrafficChannel[]
}

export type CrmVkIntegrationContextValue = {
  isLoading: boolean
} & VkIntegrationLeadChatAvailability

const CRM_VK_INTEGRATION_LOADING: CrmVkIntegrationContextValue = {
  isLoading: true,
  ...VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
}

interface CRMContextType {
  preloadingData: PreloadingData
  currentTab: LeadState
  wazzupIntegrationsData: WazzupGetFields | undefined
  vkIntegration: CrmVkIntegrationContextValue
}

const CRMContext = createContext<CRMContextType | undefined>(undefined)

interface CRMProviderProps {
  children: ReactNode
  currentTab: LeadState
}

export const CRMProvider: React.FC<CRMProviderProps> = ({
  children,
  currentTab,
}) => {
  const addAlert = useAddAlert()
  const trafficChannelsState = useAsync(crmTrafficChannelApi.getAll, [])
  const managersState = useAsync(
    () => crmApi.getAllManagers({ role: 'sales-manager' }),
    [],
  )
  const fieldsState = useAsync(crmApi.getAllFields, [])
  const statusesState = useAsync(
    async () => await crmApi.getAllStatuses({ type: 'all' }),
    [],
  )
  const funnelsState = useAsync(async () => await crmApi.funnels.getAllFunnels(), [])

  const preloadingData: PreloadingData = {
    managers: managersState.value?.data?.data || [],
    statuses: statusesState.value?.data?.data || [],
    fields: fieldsState.value?.data?.data || [],
    funnels: funnelsState.value?.data?.data?.funnels || [],
    trafficChannels: trafficChannelsState.value?.data?.data || [],
  }

  useEffect(() => {
    if (managersState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки менеджеров',
        type: 'error',
      })
    }
  }, [managersState.error])

  useEffect(() => {
    if (trafficChannelsState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки каналов трафика',
        type: 'error',
      })
    }
  }, [trafficChannelsState.error])

  useEffect(() => {
    if (statusesState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки статусов',
        type: 'error',
      })
    }
  }, [statusesState.error])

  useEffect(() => {
    if (fieldsState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки полей',
        type: 'error',
      })
    }
  }, [fieldsState.error])

  useEffect(() => {
    if (funnelsState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки воронок',
        type: 'error',
      })
    }
  }, [funnelsState.error])

  const wazzupIntegrationsState = useAsync(wazzupIntegrationApi.info, [])
  const wazzupIntegrationsData = (wazzupIntegrationsState?.value?.data ??
    undefined) as WazzupGetFields | undefined

  const { isLoading: isVkIntegrationLoading, vkLeadChat } =
    useCrmIntegrationChatAvailability()

  const vkIntegration: CrmVkIntegrationContextValue = isVkIntegrationLoading
    ? CRM_VK_INTEGRATION_LOADING
    : {
        isLoading: false,
        ...vkLeadChat,
      }

  return (
    <CRMContext.Provider
      value={{
        preloadingData,
        currentTab,
        wazzupIntegrationsData,
        vkIntegration,
      }}
    >
      {children}
    </CRMContext.Provider>
  )
}

export const useCheckCRMContext = (): boolean => {
  const context = useContext(CRMContext)
  return context !== undefined
}

export const useCRMContext = (): CRMContextType => {
  const context = useContext(CRMContext)
  if (context === undefined) {
    throw new Error('useCRMContext must be used within a CRMProvider')
  }
  return context
}